<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl font-bold text-gray-900 mb-4">Privacy Notice</h1>
        <p class="text-lg text-gray-600">Last updated: {{ lastUpdated }}</p>
      </div>

      <!-- Content -->
      <div class="bg-white shadow-lg rounded-lg p-8 prose prose-lg max-w-none">
        <!-- Introduction -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            1. Introduction
          </h2>
          <p class="text-gray-700 leading-relaxed">
            NeibrPay ("we," "our," or "us") is committed to protecting your
            privacy. This Privacy Notice explains how we collect, use, disclose,
            and safeguard your information when you use our HOA management
            platform and services (collectively, the "Service").
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            Please read this Privacy Notice carefully. If you do not agree with
            the terms of this Privacy Notice, please do not access the Service.
          </p>
        </section>

        <!-- Information We Collect -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            2. Information We Collect
          </h2>

          <h3 class="text-xl font-medium text-gray-900 mb-3">
            2.1 Personal Information
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may collect personal information that you voluntarily provide to
            us when you:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Create an account or register for the Service</li>
            <li>Use our Service features</li>
            <li>Contact us for support</li>
            <li>Participate in surveys or promotions</li>
          </ul>

          <p class="text-gray-700 leading-relaxed mt-4">
            This information may include:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              <strong>Contact Information:</strong> Name, email address, phone
              number, mailing address
            </li>
            <li>
              <strong>Account Information:</strong> Username, password, profile
              picture
            </li>
            <li>
              <strong>Property Information:</strong> Property address, unit
              number, ownership details
            </li>
            <li>
              <strong>Financial Information:</strong> Payment methods, billing
              address, transaction history
            </li>
            <li>
              <strong>Communication Preferences:</strong> Notification settings,
              communication preferences
            </li>
          </ul>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            2.2 Automatically Collected Information
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may automatically collect certain information when you use our
            Service:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              <strong>Usage Data:</strong> Information about how you use the
              Service
            </li>
            <li>
              <strong>Device Information:</strong> IP address, browser type,
              operating system
            </li>
            <li>
              <strong>Log Data:</strong> Access times, pages viewed, features
              used
            </li>
            <li>
              <strong>Cookies and Tracking:</strong> Information collected
              through cookies and similar technologies
            </li>
          </ul>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            2.3 Information from Third Parties
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may receive information about you from third parties, including:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Payment processors</li>
            <li>
              Social media platforms (if you choose to connect your account)
            </li>
            <li>Public records and databases</li>
            <li>Other users of the Service</li>
          </ul>
        </section>

        <!-- How We Use Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            3. How We Use Your Information
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We use the information we collect for various purposes, including:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>
              <strong>Service Provision:</strong> To provide, maintain, and
              improve our Service
            </li>
            <li>
              <strong>Account Management:</strong> To create and manage your
              account
            </li>
            <li>
              <strong>Communication:</strong> To send you important updates,
              notifications, and support
            </li>
            <li>
              <strong>Payment Processing:</strong> To process payments and
              manage billing
            </li>
            <li>
              <strong>Security:</strong> To protect against fraud and
              unauthorized access
            </li>
            <li>
              <strong>Legal Compliance:</strong> To comply with applicable laws
              and regulations
            </li>
            <li>
              <strong>Analytics:</strong> To analyze usage patterns and improve
              our Service
            </li>
            <li>
              <strong>Marketing:</strong> To send you promotional materials
              (with your consent)
            </li>
          </ul>
        </section>

        <!-- Information Sharing -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            4. How We Share Your Information
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We may share your information in the following circumstances:
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.1 With Your Consent
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may share your information with your explicit consent or at your
            direction.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.2 Service Providers
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may share your information with third-party service providers who
            assist us in operating our Service, including:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Payment processors</li>
            <li>Cloud storage providers</li>
            <li>Email service providers</li>
            <li>Analytics providers</li>
            <li>Customer support platforms</li>
          </ul>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.3 Legal Requirements
          </h3>
          <p class="text-gray-700 leading-relaxed">
            We may disclose your information if required to do so by law or in
            response to valid requests by public authorities.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            4.4 Business Transfers
          </h3>
          <p class="text-gray-700 leading-relaxed">
            In the event of a merger, acquisition, or sale of assets, your
            information may be transferred as part of that transaction.
          </p>
        </section>

        <!-- Data Security -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            5. Data Security
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We implement appropriate technical and organizational measures to
            protect your personal information against unauthorized access,
            alteration, disclosure, or destruction. These measures include:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication</li>
            <li>Secure data centers and infrastructure</li>
            <li>Employee training on data protection</li>
            <li>Incident response procedures</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            However, no method of transmission over the internet or electronic
            storage is 100% secure. While we strive to protect your information,
            we cannot guarantee absolute security.
          </p>
        </section>

        <!-- Data Retention -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            6. Data Retention
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We retain your personal information for as long as necessary to
            fulfill the purposes outlined in this Privacy Notice, unless a
            longer retention period is required or permitted by law. When we no
            longer need your information, we will securely delete or anonymize
            it.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            Specific retention periods may vary depending on the type of
            information and the purpose for which it was collected.
          </p>
        </section>

        <!-- Your Rights -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            7. Your Rights and Choices
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Depending on your location, you may have certain rights regarding
            your personal information:
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.1 Access and Portability
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You have the right to access and receive a copy of your personal
            information in a structured, commonly used format.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.2 Correction
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You have the right to correct inaccurate or incomplete personal
            information.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.3 Deletion
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You have the right to request deletion of your personal information,
            subject to certain exceptions.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.4 Restriction
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You have the right to restrict the processing of your personal
            information in certain circumstances.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.5 Objection
          </h3>
          <p class="text-gray-700 leading-relaxed">
            You have the right to object to the processing of your personal
            information for certain purposes.
          </p>

          <h3 class="text-xl font-medium text-gray-900 mb-3 mt-6">
            7.6 Withdraw Consent
          </h3>
          <p class="text-gray-700 leading-relaxed">
            Where processing is based on consent, you have the right to withdraw
            your consent at any time.
          </p>
        </section>

        <!-- Cookies and Tracking -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            8. Cookies and Tracking Technologies
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We use cookies and similar tracking technologies to enhance your
            experience on our Service. Cookies are small data files stored on
            your device that help us:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Remember your preferences and settings</li>
            <li>Analyze how you use our Service</li>
            <li>Provide personalized content and features</li>
            <li>Improve our Service performance</li>
          </ul>
          <p class="text-gray-700 leading-relaxed mt-4">
            You can control cookies through your browser settings, but disabling
            cookies may affect the functionality of our Service.
          </p>
        </section>

        <!-- Third-Party Services -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            9. Third-Party Services
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Our Service may contain links to third-party websites or services.
            We are not responsible for the privacy practices of these third
            parties. We encourage you to read their privacy policies before
            providing any information.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            We may integrate with the following types of third-party services:
          </p>
          <ul class="list-disc list-inside text-gray-700 mt-2 space-y-1">
            <li>Payment processors (Stripe, PayPal)</li>
            <li>Email services (SendGrid, Mailgun)</li>
            <li>Analytics providers (Google Analytics)</li>
            <li>Cloud storage providers (AWS, Google Cloud)</li>
          </ul>
        </section>

        <!-- Children's Privacy -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            10. Children's Privacy
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Our Service is not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13. If you are a parent or guardian and believe your child has
            provided us with personal information, please contact us.
          </p>
        </section>

        <!-- International Transfers -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            11. International Data Transfers
          </h2>
          <p class="text-gray-700 leading-relaxed">
            Your information may be transferred to and processed in countries
            other than your own. We ensure that such transfers comply with
            applicable data protection laws and implement appropriate safeguards
            to protect your information.
          </p>
        </section>

        <!-- Changes to Privacy Notice -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            12. Changes to This Privacy Notice
          </h2>
          <p class="text-gray-700 leading-relaxed">
            We may update this Privacy Notice from time to time. We will notify
            you of any changes by posting the new Privacy Notice on this page
            and updating the "Last updated" date.
          </p>
          <p class="text-gray-700 leading-relaxed mt-4">
            We encourage you to review this Privacy Notice periodically for any
            changes. Changes to this Privacy Notice are effective when they are
            posted on this page.
          </p>
        </section>

        <!-- Contact Information -->
        <section class="mb-8">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            13. Contact Us
          </h2>
          <p class="text-gray-700 leading-relaxed">
            If you have any questions about this Privacy Notice or our privacy
            practices, please contact us:
          </p>
          <div class="mt-4 p-4 bg-gray-50 rounded-lg">
            <p class="text-gray-700">
              <strong>Email:</strong> privacy@neibrpay.com
            </p>
            <p class="text-gray-700">
              <strong>Data Protection Officer:</strong> dpo@neibrpay.com
            </p>
            <p class="text-gray-700">
              <strong>Address:</strong> NeibrPay Privacy Team
            </p>
            <p class="text-gray-700">123 Business Street, Suite 100</p>
            <p class="text-gray-700">City, State 12345</p>
            <p class="text-gray-700"><strong>Phone:</strong> (555) 123-4567</p>
          </div>
        </section>

        <!-- Footer -->
        <div class="border-t pt-8 mt-12">
          <p class="text-sm text-gray-500 text-center">
            This Privacy Notice is effective as of {{ lastUpdated }} and will
            remain in effect except with respect to any changes in its
            provisions in the future, which will be in effect immediately after
            being posted on this page.
          </p>
        </div>
      </div>

      <!-- Back Button -->
      <div class="text-center mt-8">
        <button
          @click="goBack"
          class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
        >
          <svg
            class="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Dashboard
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';

const router = useRouter();

// Last updated date
const lastUpdated = 'September 30, 2025';

// Go back to previous page or dashboard
const goBack = () => {
  if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push('/');
  }
};
</script>

<style scoped>
.prose {
  color: #374151;
}

.prose h2 {
  color: #111827;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.prose h3 {
  color: #111827;
}

.prose ul {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.prose li {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}
</style>
